'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAppStore } from '@/store/app';
import {
  strategyCopy,
  RISK_COLOR,
  riskLabel,
  type TemplateDto,
  type PaperAccount,
  type PaperState,
} from '@/lib/strategy';
import StrategyAdvisor from '@/components/StrategyAdvisor';

const COMPARE_MAX = 3;

type UseFilter = 'all' | 'trend' | 'reversion' | 'grid' | 'accum' | 'multi';
type MarketFilter = 'all' | 'spot' | 'futures';

const USE_KEYS: UseFilter[] = ['all', 'trend', 'reversion', 'grid', 'accum', 'multi'];

function useOf(key: string): Exclude<UseFilter, 'all'> {
  const c = key.charAt(0).toLowerCase();
  if (c === 't') return 'trend';
  if (c === 'r') return 'reversion';
  if (c === 'g') return 'grid';
  if (c === 'a') return 'accum';
  return 'multi';
}

function fmtPct(v: number | undefined | null) {
  if (v == null || !isFinite(v)) return '—';
  const s = v > 0 ? '+' : '';
  return s + v.toFixed(2) + '%';
}

function fmtDate(ts: number | string | null | undefined) {
  if (ts == null) return '—';
  const n = typeof ts === 'string' ? (isNaN(Number(ts)) ? Date.parse(ts) : Number(ts)) : ts;
  if (!isFinite(n) || n <= 0) return '—';
  const d = new Date(n);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function fmtTime(ts: number | string | null | undefined) {
  if (ts == null) return '—';
  const n = typeof ts === 'string' ? (isNaN(Number(ts)) ? Date.parse(ts) : Number(ts)) : ts;
  if (!isFinite(n) || n <= 0) return '—';
  const d = new Date(n);
  const p = (n2: number) => String(n2).padStart(2, '0');
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const ACTIVE_PAPER: PaperState[] = ['created', 'running', 'paused'];

export default function StrategyPage() {
  const { language, user } = useAppStore();
  const zh = language !== 'en';
  const t = strategyCopy(zh);
  const router = useRouter();
  const [picked, setPicked] = useState<string[]>([]);
  const [advisorOpen, setAdvisorOpen] = useState(false);
  const [tab, setTab] = useState<'templates' | 'mySims' | 'backtests'>('templates');
  const [use, setUse] = useState<UseFilter>('all');
  const [market, setMarket] = useState<MarketFilter>('all');
  const [symbol, setSymbol] = useState('all');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['strategy-templates'],
    queryFn: () => api.get<TemplateDto[]>('/strategy/templates'),
  });

  const { data: papers } = useQuery({
    queryKey: ['strategy-papers'],
    queryFn: () => api.get<PaperAccount[]>('/strategy/paper'),
    enabled: !!user && tab === 'mySims',
    refetchInterval: 20000,
  });

  const { data: backtests } = useQuery({
    queryKey: ['strategy-backtests'],
    queryFn: () => api.get<Array<{ id: string; templateKey: string; symbol: string | null; interval: string; state: string; capital: number; createdAt: string; totalReturnPct: number | null; benchmarkReturnPct: number | null }>>('/strategy/backtest?limit=30'),
    enabled: !!user && tab === 'backtests',
  });

  // P1 为共享资金组合能力（文档 P6），不计入 12 类模板网格；独立入口见 S07 组合页
  const templates = (data ?? []).filter((x) => x.isActive && x.key !== 'p1');
  const symbols = Array.from(new Set(templates.flatMap((m) => m.supportedSymbols))).slice(0, 8);
  const filtered = templates.filter((m) => {
    if (use !== 'all' && useOf(m.key) !== use) return false;
    const isFutures = m.key === 't1' || m.useFutures;
    if (market !== 'all') {
      if (market === 'futures' && !isFutures) return false;
      if (market === 'spot' && isFutures) return false;
    }
    if (symbol !== 'all' && !m.supportedSymbols.includes(symbol)) return false;
    return true;
  });

  const togglePick = (key: string) => {
    setPicked((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= COMPARE_MAX) return prev;
      return [...prev, key];
    });
  };

  const goCompare = () => {
    if (picked.length < 2) return;
    router.push(`/strategy/compare?items=${picked.join(',')}`);
  };

  const nameOf = (key: string) => templates.find((m) => m.key === key)?.name;

  const tabBtn = (k: 'templates' | 'mySims' | 'backtests', label: string) => (
    <button
      key={k}
      type="button"
      onClick={() => setTab(k)}
      className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
        tab === k ? 'bg-gf-surface2 text-gf-text shadow-sm ring-1 ring-gf-border' : 'text-gf-muted hover:text-gf-text'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 pb-16 pt-6">
      {/* 标题区：策略实验室 */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gf-text">{t.title}</h1>
          <p className="mt-1 text-sm text-gf-muted">{t.labTagline}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            href="/strategy/p1"
            className="rounded-lg border border-gf-accent/50 bg-gf-accent/10 px-4 py-2 text-sm font-medium text-gf-accent transition-colors hover:bg-gf-accent/20"
          >
            {zh ? '风险平衡组合' : 'Risk-balanced'}
          </Link>
          <button
            onClick={() => setAdvisorOpen(true)}
            className="rounded-lg bg-gf-accent px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            {zh ? 'AI 帮我选' : 'Find for me'}
          </button>
          <button
            onClick={goCompare}
            disabled={picked.length < 2}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
              picked.length >= 2
                ? 'border-gf-accent bg-gf-accent/10 text-gf-accent hover:bg-gf-accent/15'
                : 'cursor-not-allowed border-gf-border bg-gf-surface2 text-gf-muted opacity-50'
            }`}
          >
            {zh ? '对比勾选' : 'Compare'} {picked.length > 0 ? `(${picked.length}/${COMPARE_MAX})` : ''}
          </button>
          {user && (
            <Link href="/strategy/papers" className="rounded-lg border border-gf-border bg-gf-surface2 px-4 py-2 text-sm font-medium text-gf-text transition-colors hover:border-gf-accent/50">
              {t.myPapers}
            </Link>
          )}
          <Link href="/strategy/universes?templateKey=t1&batch=1" className="rounded-lg border border-gf-border bg-gf-surface2 px-4 py-2 text-sm font-medium text-gf-text transition-colors hover:border-gf-accent/50">
            {zh ? '批量回测' : 'Batch backtest'}
          </Link>
          <Link href="/strategy/signals/analysis" className="rounded-lg border border-gf-border bg-gf-surface2 px-4 py-2 text-sm font-medium text-gf-text transition-colors hover:border-gf-accent/50">
            {zh ? '信号分析' : 'Signal analysis'}
          </Link>
          <Link href="/strategy/portfolios/analysis" className="rounded-lg border border-gf-border bg-gf-surface2 px-4 py-2 text-sm font-medium text-gf-text transition-colors hover:border-gf-accent/50">
            {zh ? '组合归因' : 'Portfolio attribution'}
          </Link>
        </div>
      </div>

      <StrategyAdvisor open={advisorOpen} onClose={() => setAdvisorOpen(false)} zh={zh} />

      {/* 标签页：模板库 / 我的模拟 / 回测记录 */}
      <div className="mt-5 flex items-center gap-1 border-b border-gf-border pb-1">
        {tabBtn('templates', t.tabTemplates)}
        {tabBtn('mySims', t.tabMySims)}
        {tabBtn('backtests', t.tabBacktests)}
        <Link href="/help" className="ml-auto px-2 py-2 text-sm text-gf-muted transition-colors hover:text-gf-accent">
          {t.viewResearchMethod} →
        </Link>
      </div>

      {/* ===== 模板库 ===== */}
      {tab === 'templates' && (
        <>
          {/* 筛选栏 */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-sm text-gf-muted">
              {t.filterUse}
              <select value={use} onChange={(e) => setUse(e.target.value as UseFilter)} className="rounded-lg border border-gf-border bg-gf-surface2 px-2 py-1.5 text-sm text-gf-text focus:border-gf-accent/60 focus:outline-none">
                {USE_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {k === 'all' ? t.useAll : k === 'trend' ? t.useTrend : k === 'reversion' ? t.useReversion : k === 'grid' ? t.useGrid : k === 'accum' ? t.useAccum : t.useMulti}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-sm text-gf-muted">
              {t.filterMarket}
              <select value={market} onChange={(e) => setMarket(e.target.value as MarketFilter)} className="rounded-lg border border-gf-border bg-gf-surface2 px-2 py-1.5 text-sm text-gf-text focus:border-gf-accent/60 focus:outline-none">
                <option value="all">{t.useAll}</option>
                <option value="spot">{t.spotBadge}</option>
                <option value="futures">{t.futuresBadge}</option>
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-sm text-gf-muted">
              {t.filterSymbol}
              <select value={symbol} onChange={(e) => setSymbol(e.target.value)} className="rounded-lg border border-gf-border bg-gf-surface2 px-2 py-1.5 text-sm text-gf-text focus:border-gf-accent/60 focus:outline-none">
                <option value="all">{t.useAll}</option>
                {symbols.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <span className="text-xs text-gf-muted">{filtered.length} / {templates.length}</span>
          </div>

          {isLoading && <div className="py-16 text-center text-sm text-gf-muted">{t.loading}</div>}
          {isError && <div className="py-16 text-center text-sm text-gf-muted">{zh ? '载入失败，请稍后重试' : 'Failed to load, please retry later'}</div>}

          {!isLoading && !isError && (
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 lg:gap-4">
              {filtered.map((m) => {
                const isPicked = picked.includes(m.key);
                const isFutures = m.key === 't1' || m.useFutures;
                return (
                  <div
                    key={m.key}
                    className={`group relative flex flex-col rounded-2xl border bg-gf-surface2 p-4 transition-colors lg:p-5 ${
                      isPicked ? 'border-gf-accent ring-1 ring-gf-accent/40' : 'border-gf-border hover:border-gf-accent/50'
                    }`}
                  >
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePick(m.key); }}
                      aria-label={zh ? '加入对比' : 'Add to compare'}
                      className={`absolute right-3 top-3 z-10 flex h-6 w-6 items-center justify-center rounded-full border text-xs transition-colors ${
                        isPicked ? 'border-gf-accent bg-gf-accent text-white' : 'border-gf-border bg-gf-bg/60 text-gf-muted hover:border-gf-accent/50'
                      }`}
                    >
                      {isPicked ? '✓' : '+'}
                    </button>
                    <Link href={`/strategy/${m.key}`} className="flex flex-col">
                      {/* 名称 + 标签 */}
                      <div className="mb-2 flex flex-wrap items-center gap-1.5 pr-8">
                        <h3 className="text-base font-semibold text-gf-text lg:text-lg">{zh ? (m.name?.zhHans ?? m.name?.zhHant ?? '') : (m.name?.en ?? '')}</h3>
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium`} style={{ color: RISK_COLOR[m.risk], backgroundColor: RISK_COLOR[m.risk] + '1a' }}>
                          {riskLabel(zh, m.risk)}
                        </span>
                        {isFutures ? (
                          <span className="rounded bg-gf-accent/10 px-1.5 py-0.5 text-[11px] font-medium text-gf-accent">{t.futuresBadge}</span>
                        ) : (
                          <span className="rounded bg-gf-border/40 px-1.5 py-0.5 text-[11px] font-medium text-gf-muted">{t.spotBadge}</span>
                        )}
                        {m.portfolioMode ? (
                          <span className="rounded bg-gf-accent/10 px-1.5 py-0.5 text-[11px] font-medium text-gf-accent">{t.sharedFunds}</span>
                        ) : null}
                        <span className="rounded bg-gf-bg/60 px-1.5 py-0.5 text-[11px] text-gf-muted">v{m.version}</span>
                      </div>
                      {/* 研究状态徽标 */}
                      <span className="mb-2 inline-flex w-fit items-center gap-1 rounded border border-gf-warn/40 bg-gf-warn/10 px-1.5 py-0.5 text-[11px] font-medium text-gf-warn">
                        {t.researchStatus}
                      </span>
                      {/* 三段式：适用 / 规则 / 失效 */}
                      <div className="space-y-1.5 text-sm">
                        <p className="leading-snug text-gf-text"><span className="mr-1 font-medium text-gf-muted">{t.fitFor}:</span>{zh ? (m.fit?.zhHans ?? m.fit?.zhHant ?? '') : m.fit.en}</p>
                        <p className="leading-snug text-gf-muted">{zh ? (m.shortDesc?.zhHans ?? m.shortDesc?.zhHant ?? '') : m.shortDesc.en}</p>
                        <p className="leading-snug text-gf-text"><span className="mr-1 font-medium text-gf-muted">{t.watchOut}:</span>{zh ? (m.unfit?.zhHans ?? m.unfit?.zhHant ?? '') : m.unfit.en}</p>
                      </div>
                      {/* 查看规则 */}
                      <div className="mt-auto flex items-center justify-between pt-3 text-sm font-medium text-gf-accent">
                        <span>{t.viewRules} →</span>
                      </div>
                    </Link>
                  </div>
                );
              })}
              {filtered.length === 0 && !isLoading && !isError && (
                <div className="col-span-full py-16 text-center text-sm text-gf-muted">{zh ? '没有符合条件的模板' : 'No templates match the filters'}</div>
              )}
            </div>
          )}

          {/* 更多研究模块（文档 S01） */}
          <div className="mt-10">
            <div className="mb-3 text-base font-semibold text-gf-text">{t.moreModules}</div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Link href="/strategy/x1" className="group rounded-2xl border border-gf-border bg-gf-surface2 p-4 transition-colors hover:border-gf-accent/50">
                <div className="font-medium text-gf-text">{t.moduleP1}</div>
                <p className="mt-1 text-sm leading-relaxed text-gf-muted">{t.moduleP1Desc}</p>
                <div className="mt-2 text-sm font-medium text-gf-accent">{t.moduleP1Link}</div>
              </Link>
              <Link href="/strategy/t1" className="group rounded-2xl border border-gf-border bg-gf-surface2 p-4 transition-colors hover:border-gf-accent/50">
                <div className="font-medium text-gf-text">{t.moduleF1}</div>
                <p className="mt-1 text-sm leading-relaxed text-gf-muted">{t.moduleF1Desc}</p>
                <div className="mt-2 text-sm font-medium text-gf-accent">{t.moduleF1Link}</div>
              </Link>
            </div>
            <div className="mt-6 rounded-xl border border-gf-border bg-gf-surface2 px-4 py-3 text-sm">
              <span className="font-semibold text-gf-text">{zh ? templates.length + '类研究模板' : templates.length + ' research templates'}：</span>
              <span className="text-gf-muted">{t.twelveDetail}</span>
            </div>
          </div>
        </>
      )}

      {/* ===== 我的模拟 ===== */}
      {tab === 'mySims' && (
        <div className="mt-4">
          {!user ? (
            <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-10 text-center text-sm text-gf-muted">{t.loginRequired}</div>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm text-gf-muted">{(papers ?? []).length} {zh ? '个账户' : 'accounts'}</span>
                <Link href="/strategy/papers" className="text-sm font-medium text-gf-accent">{t.myPapers} →</Link>
              </div>
              {(papers ?? []).length === 0 ? (
                <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-10 text-center text-sm text-gf-muted">{t.noPapers}</div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-gf-border bg-gf-surface2">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b border-gf-border text-left text-xs uppercase tracking-wide text-gf-muted">
                        <th className="px-4 py-3 font-medium">{t.btTemplate}</th>
                        <th className="px-4 py-3 font-medium">{t.symbol}</th>
                        <th className="px-4 py-3 font-medium">{t.status}</th>
                        <th className="px-4 py-3 font-medium">{t.currentEquity}</th>
                        <th className="px-4 py-3 font-medium">{t.returnPct}</th>
                        <th className="px-4 py-3 font-medium">{t.lastEval}</th>
                        <th className="px-4 py-3 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(papers ?? []).slice(0, 20).map((p) => {
                        const n = nameOf(p.templateKey);
                        return (
                          <tr key={p.id} className="border-b border-gf-border/60 last:border-0">
                            <td className="px-4 py-3 font-medium text-gf-text">{n ? (zh ? (n?.zhHans ?? n?.zhHant ?? '') : (n?.en ?? '')) : p.templateKey}</td>
                            <td className="px-4 py-3 text-gf-muted">{p.symbol} · {p.interval}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                                ACTIVE_PAPER.includes(p.state) ? 'text-gf-up' : p.state === 'paused' ? 'text-gf-warn' : 'text-gf-muted'
                              }`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${p.state === 'running' ? 'bg-gf-up' : p.state === 'paused' ? 'bg-gf-warn' : 'bg-gf-muted'}`} />
                                {p.state === 'running' ? t.stRunning : p.state === 'paused' ? t.stPaused : p.state === 'created' ? t.stCreated : t.stStopped}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gf-text">{p.currentEquity.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className={`px-4 py-3 font-medium ${(p.returnPct ?? 0) >= 0 ? 'text-gf-up' : 'text-gf-down'}`}>{fmtPct(p.returnPct)}</td>
                            <td className="px-4 py-3 text-gf-muted">{p.lastEvalAt ? fmtTime(p.lastEvalAt) : t.waitingFirstEval}</td>
                            <td className="px-4 py-3 text-right">
                              <Link href={`/strategy/paper/${p.id}`} className="text-sm font-medium text-gf-accent">{t.open} →</Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ===== 回测记录 ===== */}
      {tab === 'backtests' && (
        <div className="mt-4">
          {!user ? (
            <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-10 text-center text-sm text-gf-muted">{t.loginRequired}</div>
          ) : (
            <>
              {(backtests ?? []).length === 0 ? (
                <div className="rounded-2xl border border-gf-border bg-gf-surface2 p-10 text-center text-sm text-gf-muted">{t.noBacktests}</div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-gf-border bg-gf-surface2">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b border-gf-border text-left text-xs uppercase tracking-wide text-gf-muted">
                        <th className="px-4 py-3 font-medium">{t.btTemplate}</th>
                        <th className="px-4 py-3 font-medium">{t.btSymbol}</th>
                        <th className="px-4 py-3 font-medium">{t.btState}</th>
                        <th className="px-4 py-3 font-medium">{t.btReturn}</th>
                        <th className="px-4 py-3 font-medium">{t.vsBtc}</th>
                        <th className="px-4 py-3 font-medium">{t.btTime}</th>
                        <th className="px-4 py-3 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(backtests ?? []).map((b) => {
                        const n = nameOf(b.templateKey);
                        return (
                          <tr key={b.id} className="border-b border-gf-border/60 last:border-0">
                            <td className="px-4 py-3 font-medium text-gf-text">{n ? (zh ? (n?.zhHans ?? n?.zhHant ?? '') : (n?.en ?? '')) : b.templateKey}</td>
                            <td className="px-4 py-3 text-gf-muted">{b.symbol ?? '—'}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-medium ${
                                b.state === 'succeeded' ? 'text-gf-up' : b.state === 'failed' ? 'text-gf-down' : 'text-gf-warn'
                              }`}>
                                {b.state === 'succeeded' ? t.succeeded : b.state === 'failed' ? t.failed : b.state === 'running' ? t.running : t.queued}
                              </span>
                            </td>
                            <td className={`px-4 py-3 font-medium ${(b.totalReturnPct ?? 0) >= 0 ? 'text-gf-up' : 'text-gf-down'}`}>{fmtPct(b.totalReturnPct)}</td>
                            <td className="px-4 py-3 text-gf-muted">{fmtPct(b.benchmarkReturnPct)}</td>
                            <td className="px-4 py-3 text-gf-muted">{fmtTime(b.createdAt)}</td>
                            <td className="px-4 py-3 text-right">
                              <Link href={`/strategy/${b.templateKey}?taskId=${b.id}`} className="text-sm font-medium text-gf-accent">{t.open} →</Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <p className="mt-8 max-w-3xl text-xs leading-relaxed text-gf-muted">
        {t.backtestHint}
        <br />
        {t.paperHint}
      </p>
    </div>
  );
}
